/**
 * Pure rules for "what is CEPI answering from" (spec §9.2-9.5).
 *
 * Invariant: a source that is not CONFIRMED (fetch pending or failed) is never
 * reported as the corpus, and page context (`context`) is only ever non-null
 * for a document source, so an attachment never travels with a page document.
 */
import { isUsable, type ActiveResult } from '../services/activeResult';
import type { UnifiedChatContext } from '../hooks/useUnifiedChat';
import { isCompleteSet, type RunResult } from './runResults';
import type { PublishedContext } from './types';

export type ScopeFetch =
  | { status: 'none' }
  | { status: 'loading' }
  | { status: 'loaded'; result: ActiveResult }
  | { status: 'error' };

export type SourceKind =
  'checking' | 'error' | 'attachment' | 'unavailable' | 'corpus' | 'document';

export function effectiveSource(
  fetch: ScopeFetch,
  corpusPinned: boolean,
  published: PublishedContext | null,
): { kind: SourceKind; context: UnifiedChatContext | null } {
  if (fetch.status === 'loading') return { kind: 'checking', context: null };
  if (fetch.status === 'error') return { kind: 'error', context: null };
  if (fetch.status === 'loaded' && fetch.result.state !== 'missing') {
    return {
      kind: isUsable(fetch.result.state) ? 'attachment' : 'unavailable',
      context: null,
    };
  }
  if (corpusPinned) return { kind: 'corpus', context: null };
  if (published) return { kind: 'document', context: published.context };
  return { kind: 'corpus', context: null };
}

/** Display name of the capability that produced an attachment (rows predating the field are Investigator). */
export function attachmentKindName(result: ActiveResult): string {
  return result.capability === 'keyword' ? 'Keyword'
    : result.capability === 'cogni' ? 'Cogni' : 'Investigator';
}

/** Title of a usable attachment, kind name first; `complete` is the tick claim. */
export function attachmentTitle(result: ActiveResult): { text: string; complete: boolean } {
  const cov = result.coverage;
  const capability = result.capability || 'investigator';
  const name = attachmentKindName(result);
  const rows = result.rows ?? 0;
  // "all" is a claim, so it is only made when the capture said it could be.
  // Coverage leads for Investigator: "21 matched" alone cannot be audited.
  const label = capability === 'investigator'
    ? (cov
        ? `${cov.matched} of ${cov.analysed} matched${cov.complete ? '' : ' · partial'}`
        : 'result')
    : capability === 'keyword'
      ? `${result.proves_absence ? 'all' : 'top'} ${rows} product(s)`
      : `top ${rows} result(s)`;
  // Cogni is ranked, so never complete.
  const complete = capability === 'investigator'
    ? !!cov?.complete
    : capability === 'keyword' ? !!result.proves_absence : false;
  return { text: `${name} · ${label}`, complete };
}

export function offerButtonLabel(run: RunResult): string {
  // A later keyword page is a window in the middle of the set, not its top.
  const offset = run.kind === 'set'
    ? Number((run.request as { offset?: unknown } | undefined)?.offset) || 0
    : 0;
  if (offset > 0 && run.count > 0) {
    return `Answer from results ${offset + 1}–${offset + run.count}`;
  }
  const complete = run.kind === 'job'
    || isCompleteSet(run.capability, run.response, run.corpus);
  return complete
    ? `Answer from these ${run.count} results`
    : `Answer from the top ${run.count} results`;
}

/**
 * Whether the focused tab's run differs from the attachment (spec §9.5).
 * Sets compare the {tabId, seq} recorded at attach time; without a record
 * (after a reload) fall back to (capability, query, count). A false mismatch
 * only shows a prompt that Keep dismisses.
 */
export function isDifferentRun(
  run: RunResult,
  result: ActiveResult,
  record: { tabId: string; seq: number } | null,
  tabId: string,
): boolean {
  if (run.kind === 'job') return run.jobId !== result.job_id;
  if (record) return !(record.tabId === tabId && record.seq === run.seq);
  return !(run.capability === result.capability
    && run.queryText === result.criterion
    && run.count === result.rows);
}
